import {inject, Injectable} from '@angular/core';

import {Observable, defer, from, throwError} from 'rxjs';
import {switchMap} from 'rxjs/operators';

import {TranslateService} from '@ngx-translate/core';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';

import {Action} from '@actions/action';
import {ActionUtils as AU} from '@actions/action-utils';
import {SectionTarget} from '@actions/create-section.action';
import {AddTextSectionToLessonAction} from '@actions/add-text-section-to-lesson.action';
import {
  AddQuestionSectionInput,
  AddQuestionSectionToLessonAction,
} from '@actions/add-question-section-to-lesson.action';
import {AddTableSectionToLessonAction} from '@actions/add-table-section-to-lesson.action';
import {AddCollectDataSectionToLessonAction} from '@actions/add-collect-data-section-to-lesson.action';
import {AddMatchingSectionToLessonAction} from '@actions/add-matching-section-to-lesson.action';
import {EntryStore} from '@stores/entry.store';
import {RouteStore} from '@stores/route.store';
import {UserStore} from '@stores/user.store';
import {ProtoCard} from '@models/proto-card-types';

/** Card types this action can add (visualizations need a device and go through their own action). */
export const LESSON_CARD_TYPES: ReadonlyArray<ProtoCard['type']> = [
  'text-section',
  'multiple-choice',
  'free-response',
  'data-table',
  'collect-data',
  'matching',
];

export interface AddProtoCardInput {
  card: ProtoCard;
  target?: SectionTarget;
}

/** Adds a card the lesson chat proposed to a lesson, as the matching kind of section. */
@Injectable({providedIn: 'root'})
export class AddProtoCardToLessonAction implements Action<
  AddProtoCardInput,
  PL2.AnySectionEntry
> {
  private _addText = inject(AddTextSectionToLessonAction);
  private _addQuestion = inject(AddQuestionSectionToLessonAction);
  private _addTable = inject(AddTableSectionToLessonAction);
  private _addCollectData = inject(AddCollectDataSectionToLessonAction);
  private _addMatching = inject(AddMatchingSectionToLessonAction);
  private _entryStore = inject(EntryStore);
  private _routeStore = inject(RouteStore);
  private _userStore = inject(UserStore);
  private _translate = inject(TranslateService);

  execute({card, target}: AddProtoCardInput): Observable<PL2.AnySectionEntry> {
    // defer: some actions throw synchronously (e.g. an unknown device)
    return defer(() =>
      target ? this._add(card, target) : this._addToOpenLesson(card),
    );
  }

  /** Without a target the section goes into the lesson in the route: only if the user can edit it. */
  private _addToOpenLesson(card: ProtoCard): Observable<PL2.AnySectionEntry> {
    return from(
      AU.ensureCanCreateContent(
        sectionTypeOf(card),
        this._entryStore.state(),
        this._routeStore.state(),
        this._userStore.state(),
        this._translate.instant(AU.LESSON_NOT_EDITABLE_KEY),
      ),
    ).pipe(switchMap(() => this._add(card)));
  }

  private _add(
    card: ProtoCard,
    target?: SectionTarget,
  ): Observable<PL2.AnySectionEntry> {
    const d = card.data;
    const title = typeof d['title'] === 'string' ? d['title'] : '';
    switch (card.type) {
      case 'text-section':
        if (typeof d['markdown'] !== 'string') {
          return invalid(card);
        }
        return this._addText.execute({title, markdown: d['markdown']}, target);
      case 'multiple-choice':
      case 'free-response': {
        const input = questionInput(card, title);
        return input ? this._addQuestion.execute(input, target) : invalid(card);
      }
      case 'data-table':
        if (!Array.isArray(d['columns'])) {
          return invalid(card);
        }
        return this._addTable.execute(
          {
            title,
            columns: d['columns'] as string[],
            rows: (d['rows'] as string[][]) ?? [],
          },
          target,
        );
      case 'collect-data': {
        const setup = d['setup'] as APIM.AgentCollectionSetup | undefined;
        return setup
          ? this._addCollectData.execute({title, setup}, target)
          : invalid(card);
      }
      case 'matching': {
        const scene = d['scene'] as APIM.AgentMatchingScene | undefined;
        return scene
          ? this._addMatching.execute({title, scene}, target)
          : invalid(card);
      }
      default:
        return throwError(
          () => new Error(`Cards of type "${card.type}" can't be added here`),
        );
    }
  }
}

function sectionTypeOf(card: ProtoCard): PL2.EntryType {
  switch (card.type) {
    case 'multiple-choice':
    case 'free-response':
      return PL2.EntryType.QuestionSection;
    case 'data-table':
      return PL2.EntryType.SharedDataTableSection;
    case 'collect-data':
      return PL2.EntryType.CollectDataSection;
    case 'matching':
      return PL2.EntryType.SharedLineMatchingSection;
    default:
      return PL2.EntryType.TextSection;
  }
}

function questionInput(
  card: ProtoCard,
  title: string,
): AddQuestionSectionInput | undefined {
  const d = card.data;
  const prompt = typeof d['prompt'] === 'string' ? d['prompt'] : '';
  if (!prompt) {
    return undefined;
  }
  return card.type === 'multiple-choice'
    ? {
        type: 'multiple-choice',
        title,
        prompt,
        options: (d['options'] as string[]) ?? [],
        correct: (d['correct'] as number[]) ?? [],
        multiSelect: d['multiSelect'] === true,
        ...(Array.isArray(d['hints']) ? {hints: d['hints'] as string[]} : {}),
      }
    : {
        type: 'free-response',
        title,
        prompt,
        ...(typeof d['hint'] === 'string' ? {hint: d['hint']} : {}),
      };
}

function invalid(card: ProtoCard): Observable<never> {
  return throwError(
    () => new Error(`The "${card.type}" card is missing its content`),
  );
}
